import { symbolToken } from '@servicetitan/react-ioc';
import { To } from 'react-router-dom';
import { PortalUrlParams, PortalQueryParams } from '../enum/url';

export interface PortalMode {
    view: 'mobile' | 'tablet' | 'desktop_narrow' | 'desktop_wide';
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
    desktopNarrow: boolean;
    desktopWide: boolean;
    sidebarMenu: boolean;
    touch: boolean;
}

export interface PortalUserData {
    id: number;
    name: string;
    isResidential: boolean;
}

export class PortalUser {
    get id() {
        return this.data.id;
    }

    get name() {
        return this.data.name;
    }

    get isResidential() {
        return this.data.isResidential;
    }

    constructor(readonly data: PortalUserData) {}

    pickMembership = <T>(membershipValue: T, contractValue: T): T => {
        return this.isResidential ? membershipValue : contractValue;
    };
}

export interface PortalSettings {
    title: string;
    host: string;
    email: string;
    phoneNumber: string;
    phoneNumberFormatted: string;
    logo: string;
    bookingEnabled: boolean;
    timezoneCode: string;
}

export type PortalStoreEventRedirect =
    | ['dashboard']
    | ['invoice', PortalUrlParams['invoiceItem']]
    | ['invoices', PortalQueryParams['invoices']]
    | ['job', PortalUrlParams['jobItem']]
    | ['jobs', PortalQueryParams['jobs']]
    | ['jobs']
    | ['proposal', PortalUrlParams['proposalItem']]
    | ['estimate', PortalUrlParams['estimateItem']]
    | ['membership', PortalUrlParams['membershipItem']]
    | ['memberships']
    | ['service-agreements']
    | ['service-agreements', PortalUrlParams['serviceAgreementItem']]
    | ['service-agreements-esign', PortalUrlParams['serviceAgreementItem']];

export interface PortalStoreEventNotify {
    title: string;
    description: string;
    status: 'info' | 'critical' | 'success' | 'warning';
}

export interface PortalStoreEvent {
    redirect: PortalStoreEventRedirect;
    notify: string | Partial<PortalStoreEventNotify>;
    book: {};
}

export interface MicroPortalStoreEvent {
    redirect: string;
}

export interface PortalStore {
    settings?: PortalSettings;
    user?: PortalUser;
    mode: PortalMode;
    culture: Culture;

    event<K extends keyof PortalStoreEvent>(event: K, data: PortalStoreEvent[K]): void;
    onNavigate(navigate?: (path: To) => void): void;
}

export interface PortalShareData {
    settings?: PortalSettings;
    user?: PortalUser;
    culture?: Culture;
}

export interface PortalShareDataUnsafe {
    settings?: Partial<PortalSettings>;
    user?: Partial<PortalUser>;
    culture?: Culture;
}

export interface Culture extends CultureInfoData {
    PhoneFormat: PhoneFormat;
}

export interface CultureInfoData {
    Name: string;
    NumberFormat: NumberFormatInfoData;
    DateTimeFormat: DateTimeFormatInfoData;
}

export interface NumberFormatInfoData {
    CurrencyPositivePattern: string;
    CurrencyNegativePattern: string;
    CurrencySymbol: string;
    CurrencyDecimalSeparator: string;
    CurrencyGroupSeparator: string;
    CurrencyDecimalDigits: number;
    NumberDecimalSeparator: string;
    NumberGroupSeparator: string;
    NumberDecimalDigits: number;
}

export interface DateTimeFormatInfoData {
    ShortDatePattern: string;
    LongDatePattern: string;
    ShortTimePattern: string;
    DateSeparator: string;
}

export interface PhoneFormat {
    PhoneFormatInfo: {
        Pattern: RegExp;
        Format: string;
        HiddenNumberFormat?: string;
    };
    PhoneWithExtNumberFormatInfo: {
        Pattern: RegExp;
        Format: string;
        HiddenNumberFormat?: string;
    };
    PhoneMask: string;
    SimplePhoneMask: string;
    NonStrictPhoneMask: string;
    FaxMask: string;
    PhonePlaceholder: string;
    SimplePhonePlaceholder: string;
    SampleSimplePhoneNumber: string;
    FaxPlaceholder: string;
}

export const defaultCulture = (): Culture => ({
    Name: 'en-US',
    NumberFormat: {
        CurrencyPositivePattern: '%s%v',
        CurrencyNegativePattern: '%s-%v',
        CurrencySymbol: '$',
        CurrencyDecimalSeparator: '.',
        CurrencyGroupSeparator: ',',
        CurrencyDecimalDigits: 2,
        NumberDecimalSeparator: '.',
        NumberGroupSeparator: ',',
        NumberDecimalDigits: 2,
    },
    DateTimeFormat: {
        ShortDatePattern: '',
        LongDatePattern: '',
        ShortTimePattern: '',
        DateSeparator: '',
    },
    PhoneFormat: getPhoneFormatByCulture('en-US'),
});

export const portalStoreToken = symbolToken<PortalStore>('PortalStore');

const getPhonePlaceholderFromMask = (mask: string) => mask.replace(/9/g, '_').replace('?', '');

/*
 * sample digits chosen to read as an obviously-fake example number, mirroring how
 * getPhonePlaceholderFromMask above turns the same mask into a placeholder string
 */
const getSamplePhoneNumberFromMask = (mask: string) => mask.replace(/9/g, '5').replace('?', '');

export const getPhoneFormatByCulture = (name: string) => {
    const cultureNameLowerCase = name.toLowerCase();

    const is = {
        us: cultureNameLowerCase === 'en-us',
        ca: cultureNameLowerCase === 'en-ca',
        gb: cultureNameLowerCase === 'en-gb',
        au: cultureNameLowerCase === 'en-au',
        za: cultureNameLowerCase === 'en-za',
    };

    const phoneFormat: Omit<
        PhoneFormat,
        'PhonePlaceholder' | 'SimplePhonePlaceholder' | 'SampleSimplePhoneNumber' | 'FaxPlaceholder'
    > = {
        PhoneMask: '?9999999999 x99999',
        NonStrictPhoneMask: '?9999999999 x99999',
        SimplePhoneMask: '?9999999999',
        FaxMask: '?9999999999',
        PhoneFormatInfo: {
            Pattern: /(\d{10})/,
            Format: '$1',
        },
        PhoneWithExtNumberFormatInfo: {
            Pattern: /(\d{10})(\d+)/,
            Format: '$1 x$2',
        },
    };
    if (is.us || is.ca) {
        phoneFormat.PhoneMask = '(999) 999-9999? x99999';
        phoneFormat.NonStrictPhoneMask = '?(?999) 999-9999 x99999';
        phoneFormat.SimplePhoneMask = '(999) 999-9999?';
        phoneFormat.FaxMask = '(999) 999-9999';
        phoneFormat.PhoneFormatInfo = {
            Pattern: /(\d{3})(\d{3})(\d{4})/,
            Format: '($1) $2-$3',
            HiddenNumberFormat: '($1) $2-XXXX',
        };
        phoneFormat.PhoneWithExtNumberFormatInfo = {
            Pattern: /(\d{3})(\d{3})(\d{4})(\d+)/,
            Format: '($1) $2-$3 x$4',
            HiddenNumberFormat: '($1) $2-XXXX x$4',
        };
    } else if (is.gb) {
        phoneFormat.PhoneMask = '?99999999999 x99999';
        phoneFormat.NonStrictPhoneMask = '?99999999999 x99999';
        phoneFormat.SimplePhoneMask = '?99999999999';
        phoneFormat.FaxMask = '?99999999999';
        phoneFormat.PhoneFormatInfo = {
            Pattern: /(\d{3})(.*)/,
            Format: '$1$2',
            HiddenNumberFormat: '$1XXXX',
        };
        phoneFormat.PhoneWithExtNumberFormatInfo = {
            Pattern: /(\d{3})(.*)/,
            Format: '$1$2',
            HiddenNumberFormat: '$1XXXX',
        };
    }

    return {
        ...phoneFormat,
        PhonePlaceholder: getPhonePlaceholderFromMask(phoneFormat.PhoneMask),
        SimplePhonePlaceholder: getPhonePlaceholderFromMask(phoneFormat.SimplePhoneMask),
        SampleSimplePhoneNumber: getSamplePhoneNumberFromMask(phoneFormat.SimplePhoneMask),
        FaxPlaceholder: getPhonePlaceholderFromMask(phoneFormat.FaxMask),
    };
};
